/**
 * Ordre de la liste des entrees (« Sort by » de 1Password : titre, date de modification, date de creation ; « Sort »
 * de Proton Pass : plus recent, A-Z, Z-A, plus recent cree, plus ancien cree).
 *
 * Le tri se fait sur ce que le client a DEJA en clair : le nom dechiffre, et les dates `createdAt` / `updatedAt`, des
 * metadonnees que le serveur voit de toute facon (`vault-model`). Aucune donnee nouvelle ne sort du coffre.
 *
 * L'ordre choisi est une preference de cet appareil, jamais un secret : il vit en clair dans `localStorage` et se
 * relit donc comme une donnee NON FIABLE -- seuls les ordres proposes ci-dessous sont acceptes, toute autre valeur
 * retombe au defaut (nom A-Z, l'ordre historique de Klavox).
 */
import type { VaultItem } from '@klavox/vault-model';

export type EntrySortOrder = 'name-asc' | 'name-desc' | 'updated-desc' | 'created-desc' | 'created-asc';

export const ENTRY_SORT_ORDERS: readonly { readonly order: EntrySortOrder; readonly label: string }[] = [
  { order: 'name-asc', label: 'Nom (A → Z)' },
  { order: 'name-desc', label: 'Nom (Z → A)' },
  { order: 'updated-desc', label: 'Modifiees recemment' },
  { order: 'created-desc', label: 'Creees recemment' },
  { order: 'created-asc', label: 'Plus anciennes d’abord' },
];

export const ENTRY_SORT_DEFAULT: EntrySortOrder = 'name-asc';

/** Cle `localStorage` : une preference de cet appareil, jamais un secret. */
export const ENTRY_SORT_KEY = 'klavox:entry-sort';

/** L'ordre retenu ; hors des ordres proposes, le defaut (nom A-Z). */
export function entrySortOrder(raw: string | null): EntrySortOrder {
  return ENTRY_SORT_ORDERS.find(({ order }) => order === raw)?.order ?? ENTRY_SORT_DEFAULT;
}

export function entrySortLabel(order: EntrySortOrder): string {
  return ENTRY_SORT_ORDERS.find((candidate) => candidate.order === order)!.label;
}

/** Comparaison par nom, insensible a la casse et aux accents (« eBay » pres de « Ebay », « Ecole » pres de « École »). */
function compareNames(a: VaultItem, b: VaultItem): number {
  return (a.secrets.name ?? '').localeCompare(b.secrets.name ?? '', 'fr', { sensitivity: 'base', numeric: true });
}

/**
 * Comparateur de l'ordre demande. A date egale (un import cree des dizaines d'entrees dans la meme milliseconde),
 * le nom departage, puis l'identifiant : deux rendus du meme coffre donnent toujours la meme liste.
 */
export function compareEntries(order: EntrySortOrder): (a: VaultItem, b: VaultItem) => number {
  const byKey = (a: VaultItem, b: VaultItem): number => {
    switch (order) {
      case 'name-desc':
        return compareNames(b, a);
      case 'updated-desc':
        return b.updatedAt - a.updatedAt;
      case 'created-desc':
        return b.createdAt - a.createdAt;
      case 'created-asc':
        return a.createdAt - b.createdAt;
      default:
        return compareNames(a, b);
    }
  };
  return (a, b) => byKey(a, b) || compareNames(a, b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

export function sortEntries(entries: readonly VaultItem[], order: EntrySortOrder): VaultItem[] {
  return [...entries].sort(compareEntries(order));
}
